import React, { useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import HPButton from "@/components/common/Button";
import HPDropdown from "@/components/common/Dropdown";
import HPTag, { HPTagColor } from "@/components/common/Tag";
import type {
  ScorecardBrand,
  ScorecardBrandCandidate,
  ScorecardSourceQuestion,
} from "@/models/Scorecard";

interface BrandsSectionProps {
  sourceQuestion: ScorecardSourceQuestion | null;
  brandCandidates: ScorecardBrandCandidate[];
  brands: ScorecardBrand[];
  onSetPrimary: (sourceId: string) => void;
  onSetCompetitors: (sourceIds: string[]) => void;
}

const BrandsSection: React.FC<BrandsSectionProps> = ({
  sourceQuestion,
  brandCandidates,
  brands,
  onSetPrimary,
  onSetCompetitors,
}) => {
  const primary = brands.find((b) => b.role === "primary");
  const competitors = brands.filter((b) => b.role === "competitor");
  const competitorIds = competitors.map((b) => b.sourceId);

  const [pendingRemoval, setPendingRemoval] = useState<{
    brand: ScorecardBrand;
    nextIds: string[];
  } | null>(null);

  const primaryOptions = brands.map((b) => ({
    value: b.sourceId,
    label: b.label,
  }));

  const competitorOptions = brandCandidates
    .filter((c) => c.sourceId !== primary?.sourceId)
    .map((c) => ({ value: c.sourceId, label: c.label }));

  // Map competitor IDs to labels for display
  const getCompetitorLabel = (sourceId: string): string => {
    return competitors.find((b) => b.sourceId === sourceId)?.label || sourceId;
  };

  const handleCompetitorsChange = (nextIds: string[]) => {
    const removed = competitors.find((b) => !nextIds.includes(b.sourceId));
    if (removed) {
      if (competitors.length <= 1) return; // at least one Competitor required
      setPendingRemoval({ brand: removed, nextIds });
      return;
    }
    onSetCompetitors(nextIds);
  };

  const handleConfirmRemoval = () => {
    if (pendingRemoval) onSetCompetitors(pendingRemoval.nextIds);
    setPendingRemoval(null);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Brands
      </Typography>
      {sourceQuestion && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Source Brand Question: {sourceQuestion.questionTitle}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <HPDropdown
          label="Primary Brand*"
          value={primary?.sourceId ?? ""}
          onChange={onSetPrimary}
          options={primaryOptions}
          placeholder="Select Primary Brand"
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <HPDropdown
            label="Competitor Brands*"
            value={competitorIds}
            onChange={handleCompetitorsChange}
            options={competitorOptions}
            multiSelect
            countLabel="Competitor"
            placeholder="Select Competitor Brands"
          />

          {/* Read-only competitor tags display */}
          {competitorIds.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {competitorIds.map((competitorId) => (
                <HPTag
                  key={competitorId}
                  ariaLabel={getCompetitorLabel(competitorId)}
                  color={HPTagColor.BLUE}
                  hasBorder={true}
                >
                  {getCompetitorLabel(competitorId)}
                </HPTag>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={!!pendingRemoval} onClose={() => setPendingRemoval(null)}>
        <DialogTitle>Remove {pendingRemoval?.brand.label}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes {pendingRemoval?.brand.label}'s generated cells from
            every Metric Row. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <HPButton variant="text" onClick={() => setPendingRemoval(null)}>
            Cancel
          </HPButton>
          <HPButton variant="contained" color="error" onClick={handleConfirmRemoval}>
            Remove
          </HPButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandsSection;
